import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  MoreVertical,
  Eye,
  Pencil,
  Trash2,
  Building2,
  Loader2
} from 'lucide-react';

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

const TIME_ZONES = [
  { value: 'America/New_York', label: 'Eastern (EST/EDT)' },
  { value: 'America/Chicago', label: 'Central (CST/CDT)' },
  { value: 'America/Denver', label: 'Mountain (MST/MDT)' },
  { value: 'America/Los_Angeles', label: 'Pacific (PST/PDT)' },
  { value: 'America/Anchorage', label: 'Alaska (AKST/AKDT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii (HST)' }
];

const CLEANING_TYPES = ['Hopper Cars', 'Tank Cars', 'Box Cars', 'Covered Hopper Cars'];
const REPAIR_TYPES = ['Box Cars', 'Flat Cars', 'Hopper Cars', 'Tank Cars', 'Covered Hopper Cars'];

// AAR M-1002/M-1003 Certified Facility Activity Codes
const A_CODES = ['A19'];
const B_CODES = ['B24', 'B78', 'B81', 'B82', 'B85', 'B89', 'B90'];

// C-codes that have Service Equipment Categories
const C_CODES_WITH_EQUIPMENT = ['C4a', 'C4m', 'C5', 'C6i', 'C6r'];
// C-codes without Service Equipment (simple checkboxes)
const C_CODES_SIMPLE = ['C7', 'C8', 'C9', 'C10'];

// Service Equipment Categories
const SERVICE_EQUIPMENT_CATEGORIES = ['C', 'F', 'I', 'S', 'V'];

// Material Groups
const MATERIAL_GROUPS = ['MG1', 'MG2', 'MG3', 'MG4', 'MG7', 'TC128'];
const MATERIAL_GROUP_LABELS = {
  'MG1': 'MG 1',
  'MG2': 'MG 2',
  'MG3': 'MG 3',
  'MG4': 'MG 4',
  'MG7': 'MG 7',
  'TC128': 'TC-128'
};

// Repair Levels
const REPAIR_LEVEL_PRIMARY = ['RL1', 'RL2']; // Radio buttons - choose one
const REPAIR_LEVEL_ADDITIONAL = ['RLS', 'RLJ']; // Checkboxes - additive

// Empty C-code service equipment mapping
const emptyCServiceEquipment = {
  'C4a': [],
  'C4m': [],
  'C5': [],
  'C6i': [],
  'C6r': []
};

const emptyFormData = {
  name: '',
  facility_code: '',
  time_zone: 'America/Chicago',
  address: '',
  address_line_2: '',
  city: '',
  state: '',
  zip_code: '',
  contact_first_name: '',
  contact_last_name: '',
  contact_title: '',
  contact_phone: '',
  contact_email: '',
  facility_type: 'DEDICATED_MINI_SHOP',
  service_client_region: '',
  station_stencil: '',
  cleaning_types: [],
  repair_types: [],
  activity_codes: [],
  c_service_equipment: { ...emptyCServiceEquipment },
  material_groups: [],
  repair_level_primary: '',
  repair_level_additional: [],
  additional_notes: ''
};

const FacilityManagementPage = () => {
  const { api, user } = useAuth();  // Fix B: Get user for role check
  const navigate = useNavigate();
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingFacility, setEditingFacility] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(emptyFormData);
  // Two-step facility creation: when a Facility ID is being set (new facility
  // or backfilling an existing one without a code), we surface a confirmation
  // dialog before persisting because the code becomes permanently locked.
  const [pendingFacilitySave, setPendingFacilitySave] = useState(false);

  // Fix B: Check if user is Admin (can add/delete)
  const isAdmin = user?.role === 'ADMIN';

  const fetchFacilities = useCallback(async () => {
    setLoading(true);
    try {
      const params = search ? { search } : {};
      const response = await api.get('/facilities', { params });
      setFacilities(response.data);
    } catch (error) {
      console.error('Failed to fetch facilities:', error);
      toast.error('Failed to load facilities');
    } finally {
      setLoading(false);
    }
  }, [api, search]);

  useEffect(() => {
    fetchFacilities();
  }, [fetchFacilities]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchFacilities();
  };

  const handleOpenModal = (facility = null) => {
    if (facility) {
      setEditingFacility(facility);
      setFormData({
        name: facility.name || '',
        facility_code: facility.facility_code || '',
        time_zone: facility.time_zone || 'America/Chicago',
        address: facility.address || '',
        address_line_2: facility.address_line_2 || '',
        city: facility.city || '',
        state: facility.state || '',
        zip_code: facility.zip_code || '',
        contact_first_name: facility.contact_first_name || '',
        contact_last_name: facility.contact_last_name || '',
        contact_title: facility.contact_title || '',
        contact_phone: facility.contact_phone || '',
        contact_email: facility.contact_email || '',
        facility_type: facility.facility_type || 'DEDICATED_MINI_SHOP',
        service_client_region: facility.service_client_region || '',
        station_stencil: facility.station_stencil || '',
        cleaning_types: facility.cleaning_types || [],
        repair_types: facility.repair_types || [],
        activity_codes: facility.activity_codes || [],
        c_service_equipment: facility.c_service_equipment || { ...emptyCServiceEquipment },
        material_groups: facility.material_groups || [],
        repair_level_primary: facility.repair_level_primary || '',
        repair_level_additional: facility.repair_level_additional || [],
        additional_notes: facility.additional_notes || ''
      });
    } else {
      setEditingFacility(null);
      setFormData(emptyFormData);
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingFacility(null);
    setFormData(emptyFormData);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCheckboxChange = (field, value, checked) => {
    setFormData(prev => ({
      ...prev,
      [field]: checked
        ? [...prev[field], value]
        : prev[field].filter(v => v !== value)
    }));
  };

  // Handler for C-codes with equipment (C4a, C4m, C5, C6i, C6r)
  const handleCCodeWithEquipmentChange = (code, checked) => {
    setFormData(prev => {
      const newActivityCodes = checked
        ? [...prev.activity_codes, code]
        : prev.activity_codes.filter(c => c !== code);
      
      // If unchecking, clear the service equipment for this code
      const newCServiceEquipment = { ...prev.c_service_equipment };
      if (!checked) {
        newCServiceEquipment[code] = [];
      }
      
      return {
        ...prev,
        activity_codes: newActivityCodes,
        c_service_equipment: newCServiceEquipment
      };
    });
  };

  // Handler for service equipment per C-code
  const handleCCodeEquipmentChange = (cCode, equipment, checked) => {
    setFormData(prev => {
      const newCServiceEquipment = { ...prev.c_service_equipment };
      const currentEquipment = newCServiceEquipment[cCode] || [];
      
      newCServiceEquipment[cCode] = checked
        ? [...currentEquipment, equipment]
        : currentEquipment.filter(e => e !== equipment);
      
      // Auto-check the C-code if any equipment is selected
      let newActivityCodes = [...prev.activity_codes];
      if (checked && !newActivityCodes.includes(cCode)) {
        newActivityCodes.push(cCode);
      }
      
      return {
        ...prev,
        activity_codes: newActivityCodes,
        c_service_equipment: newCServiceEquipment
      };
    });
  };

  // Handler for simple C-codes (C7, C8, C9, C10)
  const handleSimpleCCodeChange = (code, checked) => {
    setFormData(prev => ({
      ...prev,
      activity_codes: checked
        ? [...prev.activity_codes, code]
        : prev.activity_codes.filter(c => c !== code)
    }));
  };

  const facilityCodeLocked = !!(editingFacility && editingFacility.facility_code);
  const isSettingFacilityCode = !facilityCodeLocked;

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.name || !formData.city || !formData.state || !formData.zip_code) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (isSettingFacilityCode) {
      const code = (formData.facility_code || '').trim();
      if (!code) {
        toast.error('Facility ID is required');
        return;
      }
      if (!/^[A-Z0-9]{2,5}$/.test(code)) {
        toast.error('Facility ID must be 2–5 uppercase letters or numbers');
        return;
      }
    }

    // Station Stencil: optional. Only enforce 4-alpha format when the user
    // actively entered a new value (different from what was stored). This
    // means legacy non-conforming records can still be saved untouched.
    const newStencil = (formData.station_stencil || '').trim();
    const oldStencil = (editingFacility?.station_stencil || '').trim();
    if (newStencil && newStencil !== oldStencil && !/^[A-Z]{4}$/.test(newStencil)) {
      toast.error('Station Stencil must be exactly 4 letters (A–Z).');
      return;
    }

    // Surface the confirmation dialog only when the Facility ID is being set
    // for the first time (new facility, or backfilling an existing one).
    if (isSettingFacilityCode) {
      setPendingFacilitySave(true);
      return;
    }

    await performSaveFacility();
  };

  const performSaveFacility = async () => {
    setPendingFacilitySave(false);
    setSaving(true);
    try {
      const payload = { ...formData };
      if (facilityCodeLocked) {
        // Server rejects any attempt to change a locked code. Strip it to be safe.
        delete payload.facility_code;
      } else if (payload.facility_code) {
        payload.facility_code = payload.facility_code.trim().toUpperCase();
      }
      if (editingFacility) {
        await api.put(`/facilities/${editingFacility.id}`, payload);
        toast.success('Facility updated successfully!');
      } else {
        await api.post('/facilities', payload);
        toast.success('Facility added successfully!');
      }
      handleCloseModal();
      fetchFacilities();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to save facility';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/facilities/${deleteId}`);
      toast.success('Facility deleted successfully');
      setDeleteId(null);
      fetchFacilities();
    } catch (error) {
      toast.error('Failed to delete facility');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="facility-management-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#323033]">Facility Management</h1>
          <p className="text-[#5A5A5A] mt-1">
            Manage your repair shops and mobile units
          </p>
        </div>
        {/* Fix B: Only show Add button for Admin */}
        {isAdmin && (
          <Button
            onClick={() => handleOpenModal()}
            className="bg-[#005EF8] hover:bg-[#0047C2] text-white"
            data-testid="add-facility-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Facility
          </Button>
        )}
      </div>

      {/* Search */}
      <form onSubmit={handleSearch}>
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#808080]" />
          <Input
            placeholder="Search facilities by name, stencil, or location..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="search-facilities-input"
          />
        </div>
      </form>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#005EF8] mx-auto"></div>
              <p className="text-[#5A5A5A] mt-4">Loading...</p>
            </div>
          ) : facilities.length === 0 ? (
            <div className="p-12 text-center">
              <Building2 className="w-12 h-12 text-[#D3D5D4] mx-auto mb-4" />
              <h3 className="text-lg font-medium text-[#323033] mb-2">No facilities yet</h3>
              <p className="text-[#5A5A5A] mb-6">
                {isAdmin ? 'Add your first facility to get started' : 'No facilities are assigned to you'}
              </p>
              {/* Fix B: Only show Add button for Admin */}
              {isAdmin && (
                <Button
                  onClick={() => handleOpenModal()}
                  className="bg-[#005EF8] hover:bg-[#0047C2] text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Facility
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Facility Name</th>
                    <th>Facility ID</th>
                    <th>Station Stencil</th>
                    <th>Type of Facility</th>
                    <th>Service Client</th>
                    <th>Contact</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {facilities.map((facility) => (
                    <tr key={facility.id} data-testid={`facility-row-${facility.id}`}>
                      <td className="font-medium text-[#323033]">{facility.name}</td>
                      <td className="font-mono text-sm">
                        {facility.facility_code
                          ? facility.facility_code
                          : <span className="text-amber-600">Not set</span>}
                      </td>
                      <td>{facility.station_stencil || 'N/A'}</td>
                      <td>
                        {facility.facility_type === 'DEDICATED_MINI_SHOP' ? 'Dedicated Mini Shop'
                          : facility.facility_type === 'FULL_SERVICE_HOME_SHOP' ? 'Full Service Home Shop'
                          : 'Mobile Railcar Repair'}
                      </td>
                      <td>{facility.service_client_region}</td>
                      <td>
                        {facility.contact_first_name} {facility.contact_last_name}
                      </td>
                      <td className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`facility-actions-${facility.id}`}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/facility-management/${facility.id}`)}>
                              <Eye className="w-4 h-4 mr-2" />
                              View
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleOpenModal(facility)}>
                              <Pencil className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            {/* Fix B: Only show Delete for Admin */}
                            {isAdmin && (
                              <DropdownMenuItem
                                onClick={() => setDeleteId(facility.id)}
                                className="text-red-600"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingFacility ? 'Edit Facility' : 'Add New Facility'}
            </DialogTitle>
            <DialogDescription>
              {editingFacility
                ? 'Update facility information'
                : 'Fill in the details to add a new facility'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Facility Information */}
            <div className="space-y-4">
              <h3 className="font-semibold text-[#323033]">Facility Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Facility Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    required
                    data-testid="facility-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="facility_code">
                    Facility ID *
                    <span
                      className="ml-1 text-xs text-[#808080] cursor-help"
                      title="Your Facility ID is a short unique code used to identify this facility in all service request numbers. This code cannot be changed after the facility is created."
                    >
                      (?)
                    </span>
                  </Label>
                  <Input
                    id="facility_code"
                    value={formData.facility_code}
                    onChange={(e) => handleChange('facility_code', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 5))}
                    placeholder="2–5 chars, A–Z 0–9"
                    minLength={2}
                    maxLength={5}
                    required
                    readOnly={facilityCodeLocked}
                    disabled={facilityCodeLocked}
                    data-testid="facility-code-input"
                  />
                  {facilityCodeLocked && (
                    <p className="text-xs text-[#808080]">
                      Facility ID is locked. Contact support for assistance.
                    </p>
                  )}
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="time_zone">Time Zone *</Label>
                  <Select
                    value={formData.time_zone}
                    onValueChange={(value) => handleChange('time_zone', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select time zone" />
                    </SelectTrigger>
                    <SelectContent>
                      {TIME_ZONES.map((tz) => (
                        <SelectItem key={tz.value} value={tz.value}>
                          {tz.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="address">Address *</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => handleChange('address', e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="address_line_2">Address Line 2</Label>
                  <Input
                    id="address_line_2"
                    value={formData.address_line_2}
                    onChange={(e) => handleChange('address_line_2', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => handleChange('city', e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="state">State *</Label>
                  <Select
                    value={formData.state}
                    onValueChange={(value) => handleChange('state', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {US_STATES.map((state) => (
                        <SelectItem key={state} value={state}>
                          {state}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="zip_code">ZIP Code *</Label>
                  <Input
                    id="zip_code"
                    value={formData.zip_code}
                    onChange={(e) => handleChange('zip_code', e.target.value)}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Primary Contact */}
            <div className="space-y-4">
              <h3 className="font-semibold text-[#323033]">Primary Point of Contact</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contact_first_name">First Name *</Label>
                  <Input
                    id="contact_first_name"
                    value={formData.contact_first_name}
                    onChange={(e) => handleChange('contact_first_name', e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact_last_name">Last Name *</Label>
                  <Input
                    id="contact_last_name"
                    value={formData.contact_last_name}
                    onChange={(e) => handleChange('contact_last_name', e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact_title">Title *</Label>
                  <Input
                    id="contact_title"
                    value={formData.contact_title}
                    onChange={(e) => handleChange('contact_title', e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact_phone">Phone *</Label>
                  <Input
                    id="contact_phone"
                    value={formData.contact_phone}
                    onChange={(e) => handleChange('contact_phone', e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="contact_email">Email *</Label>
                  <Input
                    id="contact_email"
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => handleChange('contact_email', e.target.value)}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Facility Capabilities */}
            <div className="space-y-4">
              <h3 className="font-semibold text-[#323033]">Facility Capabilities</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Type of Facility *</Label>
                  <RadioGroup
                    value={formData.facility_type}
                    onValueChange={(value) => handleChange('facility_type', value)}
                    className="flex flex-col gap-2"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="DEDICATED_MINI_SHOP" id="mini_shop" />
                      <Label htmlFor="mini_shop" className="cursor-pointer">Dedicated Mini Shop</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="MOBILE_RAILCAR_REPAIR" id="mobile" />
                      <Label htmlFor="mobile" className="cursor-pointer">Mobile Railcar Repair</Label>
                    </div>
                    {/* 2026-07-05 Wave 3 #6: new Full Service Home Shop option. */}
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="FULL_SERVICE_HOME_SHOP" id="full_service_home_shop" data-testid="facility-type-fshs" />
                      <Label htmlFor="full_service_home_shop" className="cursor-pointer">Full Service Home Shop</Label>
                    </div>
                  </RadioGroup>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="service_client_region">Service Client/Region *</Label>
                  <Input
                    id="service_client_region"
                    value={formData.service_client_region}
                    onChange={(e) => handleChange('service_client_region', e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="station_stencil">Station Stencil</Label>
                  <Input
                    id="station_stencil"
                    value={formData.station_stencil}
                    onChange={(e) => handleChange('station_stencil', e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 4))}
                    maxLength={4}
                    placeholder="4 letters (A–Z)"
                    data-testid="station-stencil-input"
                  />
                </div>
              </div>
            </div>

            {/* Optional Capabilities */}
            <div className="space-y-6">
              <div>
                <h3 className="font-semibold text-[#323033]">Optional Capabilities</h3>
                <p className="text-sm text-[#5A5A5A] mt-1">
                  Optional: Select all that apply based on facility AAR certification and repair capabilities.
                </p>
              </div>

              {/* Cleaning & Repair Types */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Cleaning Types</Label>
                  <div className="space-y-2">
                    {CLEANING_TYPES.map((type) => (
                      <div key={type} className="flex items-center space-x-2">
                        <Checkbox
                          id={`cleaning_${type}`}
                          checked={formData.cleaning_types.includes(type)}
                          onCheckedChange={(checked) => handleCheckboxChange('cleaning_types', type, checked)}
                        />
                        <Label htmlFor={`cleaning_${type}`} className="cursor-pointer">{type}</Label>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Repair Types</Label>
                  <div className="space-y-2">
                    {REPAIR_TYPES.map((type) => (
                      <div key={type} className="flex items-center space-x-2">
                        <Checkbox
                          id={`repair_${type}`}
                          checked={formData.repair_types.includes(type)}
                          onCheckedChange={(checked) => handleCheckboxChange('repair_types', type, checked)}
                        />
                        <Label htmlFor={`repair_${type}`} className="cursor-pointer">{type}</Label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* AAR M-1002/M-1003 Activity Codes */}
              <div className="space-y-3">
                <Label className="text-base font-medium">AAR M-1002/M-1003 Certified Facility Activity Code(s)</Label>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-4 bg-[#FAFAFA] border rounded-lg">
                  {/* A-Codes */}
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold text-[#323033]">A-Codes</Label>
                    <div className="space-y-2">
                      {A_CODES.map((code) => (
                        <div key={code} className="flex items-center space-x-2">
                          <Checkbox
                            id={`activity_${code}`}
                            checked={formData.activity_codes.includes(code)}
                            onCheckedChange={(checked) => handleCheckboxChange('activity_codes', code, checked)}
                          />
                          <Label htmlFor={`activity_${code}`} className="cursor-pointer text-sm">{code}</Label>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* B-Codes */}
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold text-[#323033]">B-Codes</Label>
                    <div className="space-y-2">
                      {B_CODES.map((code) => (
                        <div key={code} className="flex items-center space-x-2">
                          <Checkbox
                            id={`activity_${code}`}
                            checked={formData.activity_codes.includes(code)}
                            onCheckedChange={(checked) => handleCheckboxChange('activity_codes', code, checked)}
                          />
                          <Label htmlFor={`activity_${code}`} className="cursor-pointer text-sm">{code}</Label>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* C-Codes with Per-Code Service Equipment */}
                  <div className="space-y-3">
                    <div>
                      <Label className="text-sm font-semibold text-[#323033]">C-Codes</Label>
                      <p className="text-xs text-[#5A5A5A] mt-1">
                        For C4a, C4m, C5, C6i, C6r, select service equipment categories (C, F, I, S, V) for each code.
                      </p>
                    </div>
                    <div className="space-y-3">
                      {/* C-Codes with equipment (C4a, C4m, C5, C6i, C6r) */}
                      {C_CODES_WITH_EQUIPMENT.map((code) => (
                        <div key={code} className="space-y-1">
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id={`activity_${code}`}
                              checked={formData.activity_codes.includes(code)}
                              onCheckedChange={(checked) => handleCCodeWithEquipmentChange(code, checked)}
                            />
                            <Label htmlFor={`activity_${code}`} className="cursor-pointer text-sm font-medium">{code}</Label>
                          </div>
                          {/* Service Equipment for this C-code */}
                          <div className="ml-6 flex flex-wrap gap-2">
                            {SERVICE_EQUIPMENT_CATEGORIES.map((cat) => (
                              <div 
                                key={`${code}_${cat}`}
                                className="flex items-center space-x-1"
                              >
                                <Checkbox
                                  id={`${code}_${cat}`}
                                  checked={(formData.c_service_equipment[code] || []).includes(cat)}
                                  onCheckedChange={(checked) => handleCCodeEquipmentChange(code, cat, checked)}
                                  className="h-4 w-4"
                                />
                                <Label 
                                  htmlFor={`${code}_${cat}`} 
                                  className="cursor-pointer text-xs font-medium px-2 py-0.5 bg-white border rounded hover:bg-[#005EF8]/5"
                                >
                                  {cat}
                                </Label>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                      {/* Simple C-Codes without equipment (C7, C8, C9, C10) */}
                      <div className="border-t pt-3 mt-2">
                        {C_CODES_SIMPLE.map((code) => (
                          <div key={code} className="flex items-center space-x-2 py-1">
                            <Checkbox
                              id={`activity_${code}`}
                              checked={formData.activity_codes.includes(code)}
                              onCheckedChange={(checked) => handleSimpleCCodeChange(code, checked)}
                            />
                            <Label htmlFor={`activity_${code}`} className="cursor-pointer text-sm">{code}</Label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Material Groups */}
              <div className="space-y-3">
                <Label className="text-base font-medium">Material Group(s)</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {MATERIAL_GROUPS.map((group) => (
                    <div key={group} className="flex items-center space-x-2">
                      <Checkbox
                        id={`mg_${group}`}
                        checked={formData.material_groups.includes(group)}
                        onCheckedChange={(checked) => handleCheckboxChange('material_groups', group, checked)}
                      />
                      <Label htmlFor={`mg_${group}`} className="cursor-pointer">{MATERIAL_GROUP_LABELS[group]}</Label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Repair Level */}
              <div className="space-y-3">
                <Label className="text-base font-medium">Repair Level</Label>
                <div className="space-y-4">
                  {/* Primary Repair Level - Radio buttons */}
                  <RadioGroup
                    value={formData.repair_level_primary}
                    onValueChange={(value) => handleChange('repair_level_primary', value)}
                    className="flex flex-wrap gap-6"
                  >
                    {REPAIR_LEVEL_PRIMARY.map((level) => (
                      <div key={level} className="flex items-center space-x-2">
                        <RadioGroupItem value={level} id={`rl_${level}`} />
                        <Label htmlFor={`rl_${level}`} className="cursor-pointer">{level}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                  {/* Additional Repair Level - Checkboxes */}
                  <div className="flex flex-wrap gap-6">
                    {REPAIR_LEVEL_ADDITIONAL.map((level) => (
                      <div key={level} className="flex items-center space-x-2">
                        <Checkbox
                          id={`rl_add_${level}`}
                          checked={formData.repair_level_additional.includes(level)}
                          onCheckedChange={(checked) => handleCheckboxChange('repair_level_additional', level, checked)}
                        />
                        <Label htmlFor={`rl_add_${level}`} className="cursor-pointer">{level}</Label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* 2026-07-05 Wave 3 #7: Additional Notes — plain-text long field. */}
            <div className="space-y-2">
              <Label htmlFor="additional_notes" className="text-base font-medium">Additional Notes</Label>
              <textarea
                id="additional_notes"
                rows={5}
                maxLength={5000}
                value={formData.additional_notes || ''}
                onChange={(e) => {
                  // Strip emoji / non-BMP pictographs. Keeps punctuation, digits,
                  // whitespace and line breaks intact. No icons or emoji-style chars.
                  const cleaned = (e.target.value || '')
                    .replace(/[\u{1F300}-\u{1FAFF}\u{1F000}-\u{1F2FF}\u{2600}-\u{27BF}\u{FE0F}\u{1F1E6}-\u{1F1FF}]/gu, '');
                  handleChange('additional_notes', cleaned);
                }}
                placeholder="Any additional notes about this facility (plain text, no emojis)…"
                className="w-full rounded-md border border-input bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EF8]/40"
                data-testid="facility-additional-notes"
              />
            </div>

            <DialogFooter>
                <>
                  <Button type="button" variant="outline" onClick={handleCloseModal}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-[#005EF8] hover:bg-[#0047C2] text-white"
                    disabled={saving}
                    data-testid="save-facility-btn"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : editingFacility ? (
                      'Update Facility'
                    ) : (
                      'Add Facility'
                    )}
                  </Button>
                </>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Confirm Facility Creation (Facility ID lock) */}
      <AlertDialog open={pendingFacilitySave} onOpenChange={(open) => { if (!open) setPendingFacilitySave(false); }}>
        <AlertDialogContent data-testid="facility-code-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{editingFacility ? 'Confirm Facility ID' : 'Confirm Facility Creation'}</AlertDialogTitle>
            {/* Phase 4 Item 2 — contrast fix. AlertDialogDescription defaults
                to text-muted-foreground which is too light on white. Override
                with the darker readable body-text color used elsewhere in the app. */}
            <AlertDialogDescription className="text-[#323033]">
              {editingFacility ? (
                <>
                  You are about to set and lock the Facility ID for{' '}
                  <span className="font-medium text-[#323033]">{formData.name || 'this facility'}</span>{' '}
                  as{' '}
                  <span className="font-mono font-semibold text-[#323033]">{(formData.facility_code || '').toUpperCase()}</span>.
                </>
              ) : (
                <>
                  You are about to create{' '}
                  <span className="font-medium text-[#323033]">{formData.name || 'this facility'}</span>{' '}
                  with Facility ID{' '}
                  <span className="font-mono font-semibold text-[#323033]">{(formData.facility_code || '').toUpperCase()}</span>.
                </>
              )}
              <br /><br />
              Your Facility ID will be permanently locked and cannot be changed after this facility is created.
              This code will appear in all service request numbers for this facility.
              <br /><br />
              Please confirm this is correct before continuing.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving} data-testid="facility-code-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); performSaveFacility(); }}
              disabled={saving}
              className="bg-[#005EF8] hover:bg-[#0047C2]"
              data-testid="facility-code-confirm-btn"
            >
              {saving ? 'Saving…' : (editingFacility ? 'Confirm & Lock Facility ID' : 'Confirm & Create Facility')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Facility?</AlertDialogTitle>
            <AlertDialogDescription>
              All data within this facility (Service Requests, Vehicles, Work Schedules) will be permanently deleted. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default FacilityManagementPage;
